// lib/leasing/criterionMatch.ts
import { type ScoringCriterion } from '#types/types';

/**
 * Map whatever a model calls a criterion back to the rubric's own id.
 *
 * WHY: the Strategist is asked for `criterionId`, but the rubric is shown to it as
 * "- [20 pts] financial_health_risk (Sante Financiere & Risque : ...)", and the models answer
 * with the label, the label head before the colon, or a differently-cased id at least as often
 * as with the id itself. A strict `Set.has(id)` check drops those intents, the whole plan comes
 * out empty, and the criterion is never researched. Matching on the label costs nothing and is
 * unambiguous: labels and ids are both unique inside a rubric.
 */

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * The criterion's short name, for display: rubric labels are "Short name : long definition"
 * and only the name belongs in a table cell. The definition is doctrine, it is authored and
 * read in the doctrine editor, not repeated on every scored row, where it buried the score and
 * the reasoning under a paragraph.
 *
 * Kept separate from `labelHead` below, which normalizes for MATCHING (accents and punctuation
 * stripped) and would mangle a name shown to the user.
 */
export const criterionTitle = (label: string): string => (label.split(':')[0] ?? label).trim();

/** Label head before the first colon: rubric labels are "Short name : long definition". */
const labelHead = (label: string): string => normalize(label.split(':')[0] ?? label);

export function buildCriterionResolver(rubric: ScoringCriterion[]): (raw: unknown) => string | null {
  const byKey = new Map<string, string>();
  const remember = (key: string, id: string) => {
    if (key && !byKey.has(key)) byKey.set(key, id);
  };

  for (const criterion of rubric) {
    remember(normalize(criterion.id), criterion.id);
    remember(normalize(criterion.label), criterion.id);
    remember(labelHead(criterion.label), criterion.id);
  }

  return (raw: unknown): string | null => {
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;

    // Exact id first: never let a fuzzy rule override a literal match.
    const exact = rubric.find((criterion) => criterion.id === trimmed);
    if (exact) return exact.id;

    const key = normalize(trimmed);
    const direct = byKey.get(key) ?? byKey.get(labelHead(trimmed));
    if (direct) return direct;

    // Last resort: the model quoted a longer version of a known label (or the other way round).
    const prefixed = rubric.find((criterion) => {
      const head = labelHead(criterion.label);
      return head.length >= 6 && (key.startsWith(head) || head.startsWith(key));
    });
    return prefixed ? prefixed.id : null;
  };
}
